import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from '@mui/material';
import {
  CheckCircle,
  Security,
  Monitor,
  Support,
  CloudQueue,
  Shield,
  Speed,
  Assessment,
  ArrowForward,
  AccessTime,
  Warning,
  CheckCircleOutline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ManagedITPage: React.FC = () => {
  const navigate = useNavigate();

  const services = [
    {
      title: '24/7 Infrastructure Monitoring',
      icon: <Monitor />,
      features: [
        'Real-time performance monitoring',
        'Proactive issue detection',
        'Automated alerting system',
        'Performance optimization',
        'Capacity planning',
      ],
    },
    {
      title: 'Cybersecurity Management',
      icon: <Shield />,
      features: [
        'Threat detection and response',
        'Security patch management',
        'Firewall configuration',
        'Vulnerability assessments',
        'Compliance monitoring',
      ],
    },
    {
      title: 'Cloud Infrastructure',
      icon: <CloudQueue />,
      features: [
        'Cloud migration services',
        'Multi-cloud management',
        'Cost optimization',
        'Backup and disaster recovery',
        'Auto-scaling configuration',
      ],
    },
    {
      title: 'Help Desk Support',
      icon: <Support />,
      features: [
        'Multi-tier support levels',
        'Ticket management system',
        'Remote desktop support',
        'User training',
        'Documentation management',
      ],
    },
  ];

  const packages = [
    {
      name: 'Essential',
      price: '$2,999',
      ideal: 'Small businesses',
      features: [
        'Business hours monitoring (8x5)',
        'Basic security monitoring',
        'Monthly health reports',
        'Email support',
        '4-hour response time',
        'Up to 25 devices',
      ],
      highlighted: false,
    },
    {
      name: 'Professional',
      price: '$5,999',
      ideal: 'Growing enterprises',
      features: [
        '24/7 monitoring',
        'Advanced security suite',
        'Weekly reports & reviews',
        'Phone & email support',
        '2-hour response time',
        'Up to 100 devices',
        'Quarterly security audits',
        'Patch management',
      ],
      highlighted: true,
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      ideal: 'Government & Large Organizations',
      features: [
        '24/7 dedicated monitoring',
        'Complete security management',
        'Real-time dashboards',
        'Dedicated account manager',
        '30-minute response time',
        'Unlimited devices',
        'Monthly security audits',
        'Compliance management',
        'Custom SLAs',
      ],
      highlighted: false,
    },
  ];

  const slaMetrics = [
    { severity: 'Critical', responseTime: '30 minutes', resolutionTime: '4 hours' },
    { severity: 'High', responseTime: '1 hour', resolutionTime: '8 hours' },
    { severity: 'Medium', responseTime: '4 hours', resolutionTime: '24 hours' },
    { severity: 'Low', responseTime: '8 hours', resolutionTime: '48 hours' },
  ];

  const benefits = [
    {
      title: 'Reduce IT Costs by 40%',
      description: 'Eliminate the need for in-house IT staff and expensive infrastructure investments.',
    },
    {
      title: '99.9% Uptime Guarantee',
      description: 'Our proactive monitoring and maintenance ensure maximum system availability.',
    },
    {
      title: 'Enhanced Security Posture',
      description: 'Enterprise-grade security tools and expertise protecting your critical assets.',
    },
    {
      title: 'Scalable Solutions',
      description: 'Easily scale services up or down based on your changing business needs.',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3a8f 0%, #3b82f6 100%)',
          color: 'white',
          pt: 12,
          pb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h1" gutterBottom sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                Managed IT Services
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.95 }}>
                24/7 infrastructure monitoring, cybersecurity management, and comprehensive 
                IT support for government agencies and enterprises.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  size="large"
                  sx={{ 
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: 'grey.100',
                    }
                  }}
                  onClick={() => navigate('/contact')}
                  endIcon={<ArrowForward />}
                >
                  Get Free Assessment
                </Button>
                <Button 
                  variant="outlined" 
                  size="large"
                  sx={{ 
                    color: 'white', 
                    borderColor: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    }
                  }}
                >
                  View SLA Details
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Security sx={{ fontSize: 200, opacity: 0.3 }} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Alert Banner */}
      <Box sx={{ bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Alert 
            severity="info" 
            icon={<AccessTime />}
            sx={{ my: 2 }}
          >
            <Typography variant="body1">
              <strong>24/7 Support Available:</strong> Our security operations center monitors your 
              infrastructure round the clock. Average response time: 15 minutes.
            </Typography>
          </Alert>
        </Container>
      </Box>

      {/* Core Services */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" gutterBottom textAlign="center">
            Comprehensive IT Management
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            Full-stack IT services covering monitoring, security, support, and optimization
          </Typography>
          <Grid container spacing={4}>
            {services.map((service) => (
              <Grid item xs={12} md={6} key={service.title}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ color: 'primary.main', mr: 2 }}>
                        {service.icon}
                      </Box>
                      <Typography variant="h5" fontWeight={600}>
                        {service.title}
                      </Typography>
                    </Box>
                    <List>
                      {service.features.map((feature) => (
                        <ListItem key={feature} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckCircleOutline sx={{ fontSize: 20, color: 'success.main' }} />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Service Packages */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" gutterBottom textAlign="center">
            Service Packages
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            Flexible plans designed for organizations of all sizes
          </Typography>
          <Grid container spacing={4}>
            {packages.map((pkg) => (
              <Grid item xs={12} md={4} key={pkg.name}>
                <Card 
                  sx={{ 
                    height: '100%',
                    border: pkg.highlighted ? 2 : 0,
                    borderColor: 'primary.main',
                  }}
                >
                  <CardContent>
                    {pkg.highlighted && (
                      <Chip 
                        label="Most Popular" 
                        color="primary" 
                        size="small"
                        sx={{ mb: 2 }}
                      />
                    )}
                    <Typography variant="h4" gutterBottom>
                      {pkg.name}
                    </Typography>
                    <Typography variant="h3" color="primary" gutterBottom>
                      {pkg.price}
                      {pkg.price !== 'Custom' && <Typography variant="body2" component="span">/month</Typography>}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Ideal for: {pkg.ideal}
                    </Typography>
                    <List dense>
                      {pkg.features.map((feature) => (
                        <ListItem key={feature} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                    <Button 
                      fullWidth 
                      variant={pkg.highlighted ? 'contained' : 'outlined'}
                      sx={{ mt: 2 }}
                      onClick={() => navigate('/contact')}
                    >
                      Get Started
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* SLA Table */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h2" gutterBottom textAlign="center">
            Service Level Agreement
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 4 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Severity Level</strong></TableCell>
                  <TableCell><strong>Response Time</strong></TableCell>
                  <TableCell><strong>Resolution Time</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {slaMetrics.map((metric) => (
                  <TableRow key={metric.severity}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {metric.severity === 'Critical' && <Warning sx={{ color: 'error.main', mr: 1 }} />}
                        {metric.severity}
                      </Box>
                    </TableCell>
                    <TableCell>{metric.responseTime}</TableCell>
                    <TableCell>{metric.resolutionTime}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" gutterBottom textAlign="center">
            Why Choose Our Managed Services
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {benefits.map((benefit) => (
              <Grid item xs={12} md={6} key={benefit.title}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Assessment sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />
                    <Typography variant="h5" fontWeight={600}>
                      {benefit.title}
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {benefit.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" gutterBottom>
            Get Your Free IT Assessment
          </Typography>
          <Typography variant="h6" sx={{ mb: 4 }}>
            Discover how our managed services can transform your IT operations 
            and reduce costs by up to 40%.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              size="large"
              color="secondary"
              onClick={() => navigate('/contact')}
            >
              Schedule Free Assessment
            </Button>
            <Button 
              variant="outlined" 
              size="large"
              sx={{ 
                color: 'white',
                borderColor: 'white',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                }
              }}
            >
              Download Service Guide
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ManagedITPage;